import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import OrderDetail from '../customerProfile/OrderDetail';
import OrderService from '../order/OrderService';
import { formatDate } from '../../utils/dateUtils';

const modalStyles = {
  overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1000 },
  content: {
    top: '5%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    width: '1100px',
    maxWidth: '95vw',
    maxHeight: '90vh',
    height: 'auto',
    transform: 'translateX(-50%)',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
};

const ImageLink = ({ url, label }) => {
  if (!url) return <span className="text-gray-300">—</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline text-sm"
    >
      {label}
    </a>
  );
};

const MercariNotApprovedList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await OrderService.getMercariNotApproved();
      setOrders(res.data.orders);
    } catch {
      toast.error('โหลดข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSuccessUpdate = () => {
    setSelectedOrder(null);
    fetchData();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Mercari ไม่อนุมัติ</h1>
          <p className="text-sm text-gray-500 mt-0.5">รายการที่ตรวจแล้วและไม่ผ่านการอนุมัติ</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            ทั้งหมด{' '}
            <span className="text-xl font-bold text-red-600">{orders.length}</span>{' '}
            รายการ
          </span>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-1 text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            รีโหลด
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="min-w-full text-sm divide-y divide-gray-200">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Shop</th>
              <th className="px-4 py-3 text-left">Smile Name</th>
              <th className="px-4 py-3 text-left">Tracking JP</th>
              <th className="px-4 py-3 text-left">Verify Image 1</th>
              <th className="px-4 py-3 text-left">Verify Image 2</th>
              <th className="px-4 py-3 text-left">Confirmation Image</th>
              <th className="px-4 py-3 text-left">วันที่ตรวจ</th>
              <th className="px-4 py-3 text-left"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {orders.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                  ไม่มีรายการ
                </td>
              </tr>
            )}
            {orders.map((order, i) => (
              <tr key={order.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                <td className="px-4 py-3 text-gray-700">{order.shop || '—'}</td>
                <td className="px-4 py-3 font-medium text-gray-800">
                  {order.smileNameInput || order.CustomerProfile?.smileName || '—'}
                </td>
                <td className="px-4 py-3 font-mono text-blue-700">{order.trackingJp || '—'}</td>
                <td className="px-4 py-3">
                  <ImageLink url={order.mercariVerifyImageUrl} label="ดูรูป 1" />
                </td>
                <td className="px-4 py-3">
                  <ImageLink url={order.mercariVerifyImageUrl2} label="ดูรูป 2" />
                </td>
                <td className="px-4 py-3">
                  <ImageLink url={order.purchaseConfirmUrl} label="ดูรูปยืนยัน" />
                </td>
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                  {order.mercariVerifyAt ? formatDate(order.mercariVerifyAt) : '—'}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="text-xs px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium transition-colors"
                  >
                    เปิด Order
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={!!selectedOrder}
        onRequestClose={() => setSelectedOrder(null)}
        style={modalStyles}
        ariaHideApp={false}
      >
        {selectedOrder && (
          <OrderDetail order={selectedOrder} handleSuccessUpdate={handleSuccessUpdate} />
        )}
      </Modal>
    </div>
  );
};

export default MercariNotApprovedList;
